import React, { useState } from 'react';


const StylistPerformance = ({ data, days }) => {
  const [sortBy, setSortBy] = useState('revenue_per_hour'); // revenue_per_hour, clients_per_hour, avg_ticket, opportunity
  const [sortOrder, setSortOrder] = useState('desc'); // asc or desc

  if (!data || !data.stylists || data.stylists.length === 0) {
    return (
      <div className="stylist-performance-container">
        <div className="loading-state">No stylist data available for the selected period.</div>
      </div>
    );
  }

  const { stylists, salon_average } = data;

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Format number
  const formatNumber = (value, decimals = 2) => {
    return value.toFixed(decimals);
  };

  // Format percentage
  const formatPercent = (value) => {
    return `${value.toFixed(1)}%`;
  };

  // Get performance rating class
  const getPerformanceClass = (rating) => {
    switch(rating?.toUpperCase()) {
      case 'EXCELLENT': return 'performance-excellent';
      case 'GOOD': return 'performance-good';
      case 'NEEDS IMPROVEMENT': return 'performance-warning';
      case 'UNDERPERFORMING': return 'performance-poor';
      default: return '';
    }
  };

  // Get comparison indicator
  const getComparisonIndicator = (value, average) => {
    if (value > average * 1.1) return { icon: '↑', class: 'above-average' };
    if (value < average * 0.9) return { icon: '↓', class: 'below-average' };
    return { icon: '→', class: 'at-average' };
  };

  // Sort stylists
  const sortedStylists = [...stylists].sort((a, b) => {
    let aValue, bValue;
    
    switch(sortBy) {
      case 'revenue_per_hour':
        aValue = a.revenue_per_hour;
        bValue = b.revenue_per_hour;
        break;
      case 'clients_per_hour':
        aValue = a.clients_per_hour;
        bValue = b.clients_per_hour;
        break;
      case 'avg_ticket':
        aValue = a.avg_ticket;
        bValue = b.avg_ticket;
        break;
      case 'opportunity':
        aValue = a.opportunity_cost || 0;
        bValue = b.opportunity_cost || 0;
        break;
      default:
        aValue = a.revenue_per_hour;
        bValue = b.revenue_per_hour;
    }
    
    return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
  });

  // Handle sort change
  const handleSort = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
  };

  return (
    <div className="stylist-performance-container">
      {/* Header */}
      <div className="stylist-performance-header">
        <h2>Team Performance Analysis</h2>
        <p className="stylist-subtitle">
          Individual stylist metrics compared to salon average over the last {days} days
        </p>
      </div>

      {/* Salon Average Banner */}
      <div className="salon-average-banner">
        <h3>Salon Average Benchmarks</h3>
        <div className="average-metrics-grid">
          <div className="average-metric">
            <span className="average-label">Revenue per Hour</span>
            <span className="average-value">{formatCurrency(salon_average.revenue_per_hour)}/hr</span>
          </div>
          <div className="average-metric">
            <span className="average-label">Clients per Hour</span>
            <span className="average-value">{formatNumber(salon_average.clients_per_hour)}/hr</span>
          </div>
          <div className="average-metric">
            <span className="average-label">Average Ticket</span>
            <span className="average-value">{formatCurrency(salon_average.avg_ticket)}</span>
          </div>
          <div className="average-metric">
            <span className="average-label">Retail %</span>
            <span className="average-value">{formatPercent(salon_average.retail_pct || 0)}</span>
          </div>
        </div>
      </div>

      {/* Sort Controls */}
      <div className="sort-controls">
        <span className="sort-label">Sort by:</span>
        <div className="sort-buttons">
          <button 
            className={`sort-button ${sortBy === 'revenue_per_hour' ? 'active' : ''}`}
            onClick={() => handleSort('revenue_per_hour')}
          >
            Revenue/Hour {sortBy === 'revenue_per_hour' && (sortOrder === 'asc' ? '↑' : '↓')}
          </button>
          <button 
            className={`sort-button ${sortBy === 'clients_per_hour' ? 'active' : ''}`}
            onClick={() => handleSort('clients_per_hour')}
          >
            Clients/Hour {sortBy === 'clients_per_hour' && (sortOrder === 'asc' ? '↑' : '↓')}
          </button>
          <button 
            className={`sort-button ${sortBy === 'avg_ticket' ? 'active' : ''}`}
            onClick={() => handleSort('avg_ticket')}
          >
            Avg Ticket {sortBy === 'avg_ticket' && (sortOrder === 'asc' ? '↑' : '↓')}
          </button>
          <button 
            className={`sort-button ${sortBy === 'opportunity' ? 'active' : ''}`}
            onClick={() => handleSort('opportunity')}
          >
            Opportunity {sortBy === 'opportunity' && (sortOrder === 'asc' ? '↑' : '↓')}
          </button>
        </div>
      </div>

      {/* Stylist Cards */}
      <div className="stylists-grid">
        {sortedStylists.map((stylist) => {
          const revenueComparison = getComparisonIndicator(stylist.revenue_per_hour, salon_average.revenue_per_hour);
          const clientsComparison = getComparisonIndicator(stylist.clients_per_hour, salon_average.clients_per_hour);
          const ticketComparison = getComparisonIndicator(stylist.avg_ticket, salon_average.avg_ticket);

          return (
            <div key={stylist.employee_id} className="stylist-card">
              {/* Stylist Header */}
              <div className="stylist-card-header">
                <div className="stylist-info">
                  <h3 className="stylist-name">{stylist.employee_name || `Stylist #${stylist.employee_id}`}</h3>
                  <span className={`performance-badge ${getPerformanceClass(stylist.performance_rating)}`}>
                    {stylist.performance_rating}
                  </span>
                </div>
                {stylist.opportunity_cost > 0 && (
                  <div className="opportunity-indicator">
                    <span className="opportunity-label">Annual Opportunity:</span>
                    <span className="opportunity-amount">{formatCurrency(stylist.opportunity_cost)}</span>
                  </div>
                )}
              </div>

              {/* Key Metrics */}
              <div className="stylist-metrics-grid">
                <div className="stylist-metric">
                  <div className="metric-header-row">
                    <span className="metric-label">Revenue per Hour</span>
                    <span className={`comparison-indicator ${revenueComparison.class}`}>
                      {revenueComparison.icon}
                    </span>
                  </div>
                  <span className="metric-value">{formatCurrency(stylist.revenue_per_hour)}/hr</span>
                  <span className="vs-average">
                    vs {formatCurrency(salon_average.revenue_per_hour)} avg
                  </span>
                </div>

                <div className="stylist-metric">
                  <div className="metric-header-row">
                    <span className="metric-label">Clients per Hour</span>
                    <span className={`comparison-indicator ${clientsComparison.class}`}>
                      {clientsComparison.icon}
                    </span>
                  </div>
                  <span className="metric-value">{formatNumber(stylist.clients_per_hour)}/hr</span>
                  <span className="vs-average">
                    vs {formatNumber(salon_average.clients_per_hour)} avg
                  </span>
                </div>

                <div className="stylist-metric">
                  <div className="metric-header-row">
                    <span className="metric-label">Average Ticket</span>
                    <span className={`comparison-indicator ${ticketComparison.class}`}>
                      {ticketComparison.icon}
                    </span>
                  </div>
                  <span className="metric-value">{formatCurrency(stylist.avg_ticket)}</span>
                  <span className="vs-average">
                    vs {formatCurrency(salon_average.avg_ticket)} avg
                  </span>
                </div>

                <div className="stylist-metric">
                  <div className="metric-header-row">
                    <span className="metric-label">Retail Sales %</span>
                  </div>
                  <span className="metric-value">{formatPercent(stylist.retail_pct || 0)}</span>
                  <span className="vs-average">
                    vs {formatPercent(salon_average.retail_pct || 0)} avg
                  </span>
                </div>
              </div>

              {/* Activity Summary */}
              <div className="stylist-activity">
                <div className="activity-item">
                  <span className="activity-label">Days Worked:</span>
                  <span className="activity-value">{stylist.days_worked}</span>
                </div>
                <div className="activity-item">
                  <span className="activity-label">Total Hours:</span>
                  <span className="activity-value">{formatNumber(stylist.total_hours, 1)}</span>
                </div>
                <div className="activity-item">
                  <span className="activity-label">Total Clients:</span>
                  <span className="activity-value">{stylist.total_clients}</span>
                </div>
                <div className="activity-item">
                  <span className="activity-label">Total Revenue:</span>
                  <span className="activity-value">{formatCurrency(stylist.total_revenue)}</span>
                </div>
              </div>

              {/* Action Suggestions */}
              {stylist.suggestions && stylist.suggestions.length > 0 && (
                <div className="stylist-suggestions">
                  <h4>Recommended Actions:</h4>
                  <ul className="suggestions-list">
                    {stylist.suggestions.map((suggestion, index) => (
                      <li key={index}>{suggestion}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary Insights */}
      <div className="performance-insights">
        <h3>Team Insights</h3>
        <div className="insights-grid">
          <div className="insight-card">
            <span className="insight-icon">🌟</span>
            <div className="insight-content">
              <span className="insight-label">Top Performer</span>
              <span className="insight-value">
                {sortedStylists[0]?.employee_name || `Stylist #${sortedStylists[0]?.employee_id}`}
              </span>
              <span className="insight-detail">
                {formatCurrency(sortedStylists[0]?.revenue_per_hour)}/hr
              </span>
            </div>
          </div>

          <div className="insight-card">
            <span className="insight-icon">📈</span>
            <div className="insight-content">
              <span className="insight-label">Performance Range</span>
              <span className="insight-value">
                {formatCurrency(sortedStylists[sortedStylists.length - 1]?.revenue_per_hour)} - {formatCurrency(sortedStylists[0]?.revenue_per_hour)}
              </span>
              <span className="insight-detail">
                Revenue per hour spread
              </span>
            </div>
          </div>

          <div className="insight-card">
            <span className="insight-icon">💰</span>
            <div className="insight-content">
              <span className="insight-label">Total Opportunity</span>
              <span className="insight-value">
                {formatCurrency(
                  sortedStylists.reduce((sum, s) => sum + (s.opportunity_cost || 0), 0)
                )}
              </span>
              <span className="insight-detail">
                From bringing underperformers to average
              </span>
            </div>
          </div>

          <div className="insight-card">
            <span className="insight-icon">👥</span>
            <div className="insight-content">
              <span className="insight-label">Team Size</span>
              <span className="insight-value">{stylists.length} stylists</span>
              <span className="insight-detail">
                Over {days}-day period
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Data Note */}
      <div className="data-note">
        <p>
          <strong>Note:</strong> Opportunity costs represent the additional annual revenue 
          if an underperforming stylist were brought up to salon average productivity. 
          Comparisons are based on actual performance data from the last {days} days.
        </p>
      </div>
    </div>
  );
};

export default StylistPerformance;